import { ModuleValueGeneral, SchemaModuleGeneral } from './module-general';
import { JSONSchema } from '../types/json-schema';
import { FormComponent } from '../form.component';

import * as rawJSONSchemaModuleCancerCervical from '../../schemas/module-cancer-cervical.json';

class ModuleValueCancerCervical extends ModuleValueGeneral {
  // Step 1
  date_of_birth?: string;
  date_of_diagnosis?: string;

  // Step 2
  date_of_surgery?: string;

  // surgery
  histopathological_tumour_size_cm_surgery?: string;
  stromal_invasion_thirds_surgery?: string;
  depth_of_invasion_mm_surgery?: string;
  lymph_vascular_space_invasion_surgery?: string;
  gog_score_surgery?: string;
  sedlis_score_surgery?: string;

  // radiation

  // palliative

  // Step 3
  recurrence_progression_date?: string;
  date_last_follow_up?: string;
  follow_up_duration?: number;
  follow_up_duration_compute?: boolean;
}

export class SchemaModuleCancerCervical extends SchemaModuleGeneral<ModuleValueCancerCervical> {
  schema: JSONSchema<ModuleValueCancerCervical>;

  constructor() {
    super();

    this.loadSchema( rawJSONSchemaModuleCancerCervical );
  }

  hook( form: FormComponent ) {
    super.hook( form );

    this.listenValue( [
      'histopathological_tumour_size_cm_surgery',
      'stromal_invasion_thirds_surgery',
      'depth_of_invasion_mm_surgery',
      'lymph_vascular_space_invasion_surgery',
    ], this.fieldHandlerCalculateGogScore.bind( this ) );

    this.listenValue( [
      'histopathological_tumour_size_cm_surgery',
      'stromal_invasion_thirds_surgery',
      'lymph_vascular_space_invasion_surgery',
    ], this.fieldHandlerCalculateSedlisScore.bind( this ) );

    this.listenValue( 'follow_up_duration_compute', this.fieldHandlerFollowUpDurationCompute.bind( this ) );

    this.listenValue(
      [ 'date_last_follow_up', 'date_of_surgery' ],
      this.fieldHandlerCalculateFollowUpDuration.bind( this )
    );
  }

  fieldHandlerCalculateFollowUpDuration( followUpDate: string, startDate: string ) {
    if ( !this.getValue( 'follow_up_duration_compute' ) ) return;
    this.updateValue( 'follow_up_duration', this.diffInMonths( followUpDate, startDate, 30.5 ) );
  }

  fieldHandlerFollowUpDurationCompute( compute: boolean ) {
    if ( typeof compute === 'undefined' || compute === null ) return;
    this.schema.properties[ 'follow_up_duration' ].readOnly = compute;

    if ( compute ) {
      this.fieldHandlerCalculateFollowUpDuration(
        this.getValue( 'date_last_follow_up' ),
        this.getValue( 'date_of_surgery' ),
      );
    }
  }

  setupValidators() {
    super.setupValidators();

    this.validatorDateComparison( 'date_of_diagnosis', 'date_of_birth', '>' );

    this.validatorDateComparison( 'date_of_surgery', 'date_of_birth', '>' );
    this.validatorDateComparison( 'date_of_surgery', 'date_of_diagnosis', '>=' );

    this.validatorDateComparison( 'recurrence_progression_date', 'date_of_birth', '>' );
    this.validatorDateComparison( 'recurrence_progression_date', 'date_of_diagnosis', '>=' );
    this.validatorDateComparison( 'recurrence_progression_date', 'date_of_surgery', '>=' );

    this.validatorDateComparison( 'date_last_follow_up', 'date_of_birth', '>' );
    this.validatorDateComparison( 'date_last_follow_up', 'date_of_diagnosis', '>=' );
    this.validatorDateComparison( 'date_last_follow_up', 'date_of_surgery', '>=' );

    this.validatorDate( 'date_of_birth', ( new Date() ), '<=' , v => new Date( v as any ));
    this.validatorDate( 'date_of_diagnosis', ( new Date() ), '<=' , v => new Date( v as any ));
  }

  fieldHandlerCalculateGogScore( tumour_size: string, stromal_invasion: string, invasion_depth: string, lvsi: string ) {

    if ( !tumour_size || !stromal_invasion || !invasion_depth || !lvsi ) {
      this.updateValue( 'gog_score_surgery', '' );
      return;
    }

    const risks = {
      penetration: {
        superficial: {
          '1_mm': 1,
          '2_mm': 1,
          '3_mm': 1,
          '4_mm': 3,
          '5_mm': 7.2,
          '6_mm': 14,
          '7_mm': 21,
          '8_mm': 26,
          '9_mm': 26,
          '10_mm': 31,
          '11_mm': 31,
          '12_mm': 31,
          '13_mm': 31,
          '14_mm': 31,
          '15_mm': 31,
          '16_mm': 31,
          '17_mm': 31,
          '18_mm': 31,
          '19_mm': 31,
          '20_mm': 31,
          'gt_20_mm': 31,
        },
        middle: {
          '1_mm': 20,
          '2_mm': 20,
          '3_mm': 20,
          '4_mm': 20,
          '5_mm': 20,
          '6_mm': 22,
          '7_mm': 23,
          '8_mm': 25,
          '9_mm': 25,
          '10_mm': 28,
          '11_mm': 28,
          '12_mm': 32,
          '13_mm': 32,
          '14_mm': 36,
          '15_mm': 36,
          '16_mm': 36,
          '17_mm': 36,
          '18_mm': 36,
          '19_mm': 36,
          '20_mm': 36,
          'gt_20_mm': 36,
        },
        deep: {
          '1_mm': 28,
          '2_mm': 28,
          '3_mm': 28,
          '4_mm': 28,
          '5_mm': 28,
          '6_mm': 28,
          '7_mm': 28,
          '8_mm': 30,
          '9_mm': 30,
          '10_mm': 34,
          '11_mm': 34,
          '12_mm': 37,
          '13_mm': 37,
          '14_mm': 41,
          '15_mm': 41,
          '16_mm': 45,
          '17_mm': 45,
          '18_mm': 49,
          '19_mm': 49,
          '20_mm': 54,
          'gt_20_mm': 54,
        },
      },
      tumour_size: {
        'occult': 1,
        '1_cm': 1.6,
        '2_cm': 1.9,
        '3_cm': 2.4,
        '4_cm': 2.9,
        '5_cm': 2.9,
        '6_cm': 4.4,
        'gt_7_cm': 4.4,
      },
      lvsi: {
        unknown: 1,
        negative: 1,
        positive: 1.7,
      },
    };

    const penetration_risk = risks.penetration[ stromal_invasion ][ invasion_depth ];
    const tumour_size_risk = risks.tumour_size[ tumour_size ];
    const lvsi_risk = risks.lvsi[ lvsi ];

    const gog = penetration_risk * tumour_size_risk * lvsi_risk;

    this.updateValue( 'gog_score_surgery', gog.toFixed( 2 ) );
  }

  fieldHandlerCalculateSedlisScore( tumour_size: string, stromal_invasion: string, lvsi: string ) {

    if ( !tumour_size || !stromal_invasion || !lvsi ) {
      this.updateValue( 'sedlis_score_surgery', '' );
      return;
    }

    const tumour_size_map = {
      'occult': 0,
      '1_cm': 1,
      '2_cm': 2,
      '3_cm': 3,
      '4_cm': 4,
      '5_cm': 5,
      '6_cm': 6,
      'gt_7_cm': 7,
    };

    const tumour_size_number: number = tumour_size_map[ tumour_size ];

    const risk = (
      ( lvsi === 'positive' && stromal_invasion === 'deep' ) ||
      ( lvsi === 'positive' && stromal_invasion === 'middle' && tumour_size_number >= 2 ) ||
      ( lvsi === 'positive' && stromal_invasion === 'superficial' && tumour_size_number >= 5 ) ||
      ( lvsi === 'negative' && ( stromal_invasion === 'deep' || stromal_invasion === 'middle' ) && tumour_size_number >= 4 )
    );

    this.updateValue( 'sedlis_score_surgery', risk ? 'positive' : 'negative' );
  }
}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/forms/module-cancer-cervical.ts